import React, { useState, useEffect } from 'react';
import CarLogo from './components/CarLogo';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorIcon from './components/ErrorIcon';
import ChartIcon from './components/ChartIcon';
import API_BASE_URL from './config';

function formatDate(dateStr) {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
}

function CarSummary({ carId }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (!carId) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/car-summary/${carId}`)
      .then(res => res.json())
      .then(data => {
        setSummary(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [carId]);

  if (!carId) return (
    <div className="w-full max-w-7xl mx-auto p-6 text-center">
      <div className="card shadow-modern">
        <div className="flex justify-center mb-4 animate-floating">
          <CarLogo size="2xl" className="text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">اختر سيارة لعرض ملخص الحساب</h3>
        <p className="text-gray-500">استخدم القائمة أعلاه لاختيار السيارة المطلوبة</p>
      </div>
    </div>
  );
  
  if (loading) return (
    <div className="w-full max-w-7xl mx-auto p-6 text-center">
      <div className="card shadow-modern">
        <div className="flex justify-center mb-4">
          <LoadingSpinner size="xl" className="text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-700">جاري التحميل...</h3>
        <div className="mt-4 w-32 h-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mx-auto animate-pulse"></div>
      </div>
    </div>
  );
  
  if (!summary) return (
    <div className="w-full max-w-7xl mx-auto p-6 text-center">
      <div className="card shadow-modern">
        <div className="flex justify-center mb-4">
          <ErrorIcon size="xl" className="text-red-500" />
        </div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">لا يوجد بيانات</h3>
        <p className="text-gray-500">لم يتم العثور على بيانات لهذه السيارة</p>
      </div>
    </div>
  );

  // حساب الحركات والارصدة بعد تطبيق الفلتر الزمني
  const filteredMovements = (summary.movements || []).filter(m => {
    if (!startDate && !endDate) return true;
    const d = m.date ? new Date(m.date.split('T')[0]) : null;
    if (!d) return false;
    if (startDate) {
      const s = new Date(startDate);
      if (d < s) return false;
    }
    if (endDate) {
      const e = new Date(endDate);
      if (d > e) return false;
    }
    return true;
  });

  const totalDebit = filteredMovements.reduce((sum, m) => sum + Number(m.debit || 0), 0);
  const totalCredit = filteredMovements.reduce((sum, m) => sum + Number(m.credit || 0), 0);
  const balance = totalDebit - totalCredit;

  function downloadCSV(summaryData, movements) {
    const rows = [];
    
    // عنوان التقرير
    rows.push('"ملخص السيارة - ' + (summaryData.carName || '') + '"');
    rows.push('"تاريخ التقرير: ' + new Date().toLocaleDateString('ar-EG') + '"');
    rows.push(''); // صف فارغ
    
    // رؤوس الأعمدة
    const headers = ['رقم', 'اسم السيارة', 'الشهر', 'التاريخ', 'المسحوبات', 'الشغل', 'تفاصيل', 'تصنيف'];
    rows.push(headers.map(h => '"' + h + '"').join(','));
    
    // خط فاصل
    rows.push('"────────","────────","────────","────────","────────","────────","────────","────────"');

    // البيانات
    movements.forEach(m => {
      const values = [
        m.id,
        summaryData.carName || '',
        m.month || '',
        m.date ? formatDate(m.date) : '',
        m.credit ? Number(m.credit).toLocaleString() : '',
        m.debit ? Number(m.debit).toLocaleString() : '',
        m.details || '',
        m.category || ''
      ];
      const safe = values.map(v => {
        const s = (v === null || v === undefined) ? '' : String(v);
        return '"' + s.replace(/"/g, '""') + '"';
      });
      rows.push(safe.join(','));
    });

    // فاصل قبل الإحصائيات
    rows.push(''); 
    rows.push('"════════","════════","════════","════════","════════","════════","════════","════════"');
    rows.push(''); 
    
    // الإحصائيات
    rows.push('"","","","الإحصائيات النهائية","","","",""');
    rows.push(''); 
    
    // إجمالي المسحوبات
    const totalCreditRow = ["", "", "", "إجمالي المسحوبات", totalCredit.toLocaleString(), "", "", ""].map(v => {
      const s = (v === null || v === undefined) ? '' : String(v);
      return '"' + s.replace(/"/g, '""') + '"';
    }).join(',');
    rows.push(totalCreditRow);
    
    // إجمالي الشغل
    const totalDebitRow = ["", "", "", "إجمالي الشغل", "", totalDebit.toLocaleString(), "", ""].map(v => {
      const s = (v === null || v === undefined) ? '' : String(v);
      return '"' + s.replace(/"/g, '""') + '"';
    }).join(',');
    rows.push(totalDebitRow);
    
    // خط فاصل
    rows.push('"────────","────────","────────","────────","────────","────────","────────","────────"');
    
    // الرصيد النهائي
    const finalBalance = totalDebit - totalCredit; // الشغل - المسحوبات
    const balanceLabel = finalBalance >= 0 ? "رصيد إيجابي ✓" : "رصيد سلبي ⚠";
    const balanceRow = ["", "", "", "الرصيد النهائي", "", Math.abs(finalBalance).toLocaleString(), balanceLabel, ""].map(v => {
      const s = (v === null || v === undefined) ? '' : String(v);
      return '"' + s.replace(/"/g, '""') + '"';
    }).join(',');
    rows.push(balanceRow);
    
    // معلومات إضافية
    rows.push(''); 
    rows.push('"معلومات إضافية","","","","","","",""');
    rows.push('"عدد الحركات",' + movements.length + ',"","","","","",""');

    const csvContent = rows.join('\n');
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // إنشاء اسم الملف بتنسيق أفضل
    const carName = (summaryData.carName || 'سيارة').replace(/[^a-zA-Z0-9\u0600-\u06FF\s]/g, '').trim() || 'سيارة';
    const currentDate = new Date();
    const arabicDate = currentDate.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit'
    }).replace(/\//g, '-');
    
    a.download = `ملخص_${carName}_${arabicDate}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div dir="rtl" className="w-full max-w-7xl mx-auto p-6" style={{ direction: 'rtl', textAlign: 'right' }}>
      <div className="mb-8 animate-slideInDown">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
          ملخص السيارة: {summary.carName}
        </h2>
        <p className="text-gray-600">تقرير شامل   </p>
      </div>
      
      <div className="card shadow-modern mb-8 animate-slideInDown" style={{animationDelay: '0.1s'}}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold flex items-center gap-2">
            <span className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white text-sm">🗓️</span>
            فلتر التواريخ
          </h3>
          <button 
            className="btn-primary flex items-center gap-2" 
            onClick={() => downloadCSV(summary, filteredMovements)}
          >
            <span>📊</span>
            تحميل CSV
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">من تاريخ</label>
            <input 
              className="input-modern" 
              type="date" 
              value={startDate} 
              onChange={e => setStartDate(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">إلى تاريخ</label>
            <input 
              className="input-modern" 
              type="date" 
              value={endDate} 
              onChange={e => setEndDate(e.target.value)} 
            />
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 animate-slideInDown" style={{animationDelay: '0.2s'}}>
        <div className="card shadow-modern bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center text-white text-xl">
              📤
            </div>
            <div>
              <p className="text-sm font-medium text-red-600">إجمالي المسحوبات</p>
              <p className="text-2xl font-bold text-red-700">{totalCredit.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="card shadow-modern bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center text-white text-xl">
              📥
            </div>
            <div>
              <p className="text-sm font-medium text-green-600">إجمالي الشغل</p>
              <p className="text-2xl font-bold text-green-700">{totalDebit.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="card shadow-modern bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center text-white text-xl">
              💰
            </div>
            <div>
              <p className="text-sm font-medium text-indigo-600">الرصيد النهائي</p>
              <p className={`text-2xl font-bold ${balance >= 0 ? 'text-indigo-700' : 'text-red-600'}`}>
                {balance.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-modern animate-slideInDown" style={{animationDelay: '0.3s'}}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold flex items-center gap-2">
            <span className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-sm">📋</span>
            تفاصيل الحركات ({filteredMovements.length})
          </h3>
        </div>
        
        {filteredMovements.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="flex justify-center mb-4">
              <ChartIcon size="xl" className="text-blue-600" />
            </div>
            <p className="text-lg">لا توجد حركات في الفترة المحددة</p>
            <p className="text-sm">جرب تعديل نطاق التواريخ</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th>رقم</th>
                  <th>الشهر</th>
                  <th>التاريخ</th>
                  <th>المسحوبات</th>
                  <th>الشغل</th>
                  <th>تفاصيل</th>
                  <th>تصنيف</th>
                </tr>
              </thead>
              <tbody>
                {filteredMovements.map((m, index) => (
                  <tr key={m.id} style={{animationDelay: `${index * 50}ms`}} className="animate-slideInDown">
                    <td>
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-sm font-bold rounded-lg">
                        {m.id}
                      </span>
                    </td>
                    <td className="font-medium">{m.month}</td>
                    <td>{formatDate(m.date)}</td>
                    <td>
                      {m.credit && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                          {Number(m.credit).toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td>
                      {m.debit && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          {Number(m.debit).toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="max-w-xs truncate" title={m.details}>{m.details}</td>
                    <td>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {m.category}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CarSummary;
